<template>
  <div class="live-invite">
    <div class="live-hero">
      <div class="live-hero__copy">
        <p class="live-eyebrow">IMPROVE Live</p>
        <h1 class="live-title">Join IMPROVE Live if the tour wasn’t enough</h1>
        <p class="live-subtitle">
          Sit in with the team on a live session — we’ll walk through the
          platform, answer questions, and help you get started.
        </p>
      </div>
    </div>

    <div class="live-panel">
      <div v-if="isJoinLink" class="live-fallback">
        <div class="live-fallback__icon">
          <v-icon large>fa-video</v-icon>
        </div>
        <h2>Join the live session</h2>
        <p>
          Open the Teams or Zoom session in a new tab. Come back here when
          you’re done to continue setup.
        </p>
        <v-btn
          color="primary"
          large
          depressed
          :href="bookingUrl"
          target="_blank"
          rel="noopener"
        >
          <v-icon left small>fa-external-link-alt</v-icon>
          Join IMPROVE Live
        </v-btn>
      </div>

      <div v-else-if="bookingUrl" class="live-embed-frame">
        <iframe
          :src="bookingUrl"
          title="Book IMPROVE Live"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </div>

      <div v-else class="live-fallback">
        <div class="live-fallback__icon">
          <v-icon large>fa-video</v-icon>
        </div>
        <h2>Book a session with the team</h2>
        <!-- No booking link is set (VUE_APP_LIVE_BOOKING_URL is blank), so the
             route to a session is email. That is a fact about our setup, not
             something to tell the customer: the earlier copy said the link
             "isn't configured yet". -->
        <p>
          Email us and we’ll find a time for your IMPROVE Live session, or
          carry on setting up in the app whenever you’re ready.
        </p>
        <v-btn
          color="primary"
          large
          depressed
          href="mailto:help@improve.online?subject=Join%20IMPROVE%20Live"
        >
          <v-icon left small>fa-envelope</v-icon>
          Email help@improve.online
        </v-btn>
      </div>

      <div class="live-panel__footer">
        <v-btn text color="grey700" @click="backToSetup">Back to setup</v-btn>
        <div class="footer-right">
          <v-btn color="primary" depressed @click="continueSetup">
            Continue setup
            <v-icon right small>fa-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const LIVE_BOOKING_URL = (process.env.VUE_APP_LIVE_BOOKING_URL || "").trim();

export default {
  name: "LiveWebinarInvite",
  data() {
    return {
      bookingUrl: LIVE_BOOKING_URL
    };
  },
  computed: {
    isJoinLink() {
      const url = (this.bookingUrl || "").toLowerCase();
      if (!url) return false;
      return (
        url.indexOf("zoom.us") !== -1 ||
        url.indexOf("teams.microsoft.com") !== -1 ||
        url.indexOf("meet.google.com") !== -1
      );
    }
  },
  methods: {
    backToSetup() {
      this.$emit("back-to-setup");
    },
    continueSetup() {
      this.$emit("continue-setup");
    }
  }
};
</script>

<style scoped>
.live-invite {
  max-width: 1180px;
  margin: 0 auto;
  padding: 8px 8px 32px;
  text-align: left;
}

.live-hero {
  margin-bottom: 24px;
  padding: 28px 32px;
  border-radius: 16px;
  background:
    linear-gradient(135deg, rgba(123, 200, 163, 0.18), rgba(22, 43, 68, 0.06)),
    #fff;
  border: 1px solid rgba(22, 43, 68, 0.08);
}

.live-eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #08a0a8;
}

.live-title {
  margin: 0 0 8px;
  font-size: 32px;
  font-weight: 700;
  color: #002458;
  line-height: 1.15;
}

.live-subtitle {
  margin: 0;
  max-width: 640px;
  font-size: 16px;
  line-height: 1.5;
  color: #5a6a7a;
}

.live-panel {
  background: #fff;
  border: 1px solid rgba(22, 43, 68, 0.08);
  border-radius: 16px;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.live-embed-frame {
  position: relative;
  width: 100%;
  padding-bottom: 62%;
  height: 0;
  overflow: hidden;
  background: #002458;
  flex: 1;
}

.live-embed-frame iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.live-fallback {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
  max-width: 560px;
  margin: 0 auto;
}

.live-fallback__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: rgba(123, 200, 163, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #002458;
}

.live-fallback h2 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #002458;
}

.live-fallback p {
  margin: 0 0 24px;
  color: #5a6a7a;
  line-height: 1.5;
}

.live-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 32px 24px;
  border-top: 1px solid #eef2f5;
}

.footer-right {
  display: flex;
  align-items: center;
}

@media (max-width: 960px) {
  .live-hero {
    padding: 22px;
  }

  .live-panel__footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
